// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {IconChevronNext, IconForwardNext} from '@wandb/weave/components/Icon';
import * as _ from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown} from 'semantic-ui-react';

import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import type * as PanelBankConfigTypes from '../../../state/views/panelBankConfig/types';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {type useSectionsForMovePanel} from '../hooks/useSectionsForMovePanel';
import {usePanelContext} from '../PanelContextProvider';

type MovePanelSubmenuProps = {
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  eligibleSections: ReturnType<typeof useSectionsForMovePanel>;
  defaultMoveToSectionRef: PanelBankSectionConfigTypes.Ref | undefined;
};

// "Move panel to section..." submenu
export const MovePanelSubmenu = ({
  panelBankConfigRef,
  panelBankSectionConfigRef,
  eligibleSections,
  defaultMoveToSectionRef,
}: MovePanelSubmenuProps) => {
  const {panelRef, trackPanelControls} = usePanelContext();
  const [isOpen, setIsOpen] = useState(false);

  const panelMoverRef = useRef<HTMLDivElement>(null);

  // The "Move panel to {default section}" menu option
  const defaultSectionValue = defaultMoveToSectionRef
    ? defaultMoveToSectionRef.id
    : undefined;

  const defaultSection = defaultMoveToSectionRef
    ? eligibleSections.find(
        section => section.ref.id === defaultMoveToSectionRef.id
      )
    : null;

  const panelMoverOptions = [
    ...(defaultSection
      ? [
          {
            value: defaultSection.ref.id,
            text: defaultSection.part.name,
          },
        ]
      : []),
    ...eligibleSections
      .filter(({ref}) => ref.id !== defaultSectionValue)
      .map(({ref, part}) => ({
        value: ref.id,
        text: part.name,
      })),
  ];

  const movePanelToNewSection = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.movePanelToNewSection
  );

  const movePanel = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.movePanel
  );

  const onAddSection = useCallback(
    (newSectionName: string) => {
      movePanelToNewSection({
        fromSectionRef: panelBankSectionConfigRef,
        panelRef,
        newSectionName,
      });
      trackPanelControls({
        action: 'Panel moved to another section',
        isNewlyCreatedSection: true,
      });
    },
    [
      movePanelToNewSection,
      panelBankSectionConfigRef,
      panelRef,
      trackPanelControls,
    ]
  );

  const onChange = useCallback(
    toSectionRef => {
      movePanel(
        {
          panelRef,
          sectionRef: panelBankSectionConfigRef,
        },
        {
          panelRef: null, // move to the beginning of the section
          sectionRef: toSectionRef,
          insertDir: 'before',
        }
      );
      trackPanelControls({
        action: 'Panel moved to another section',
        isNewlyCreatedSection: false,
      });
    },
    [movePanel, panelBankSectionConfigRef, panelRef, trackPanelControls]
  );

  useEffect(() => {
    setTimeout(() => {
      panelMoverRef.current?.querySelector('input')?.focus();
    }, 1);
  }, []);

  return (
    <div className="popup-submenu" ref={panelMoverRef}>
      <DropdownMenu.Sub open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.SubTrigger data-test="panel-action-icon-move">
          <IconForwardNext />
          Move panel to section...
          <IconChevronNext className="text-moon-450" />
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent className="py-0">
            <Dropdown
              style={{border: 'none'}}
              selection
              search
              allowAdditions
              additionLabel="Create section: "
              additionPosition="bottom"
              open
              options={panelMoverOptions}
              placeholder="Find or create section..."
              selectOnNavigation={false}
              onAddItem={(e, data) => onAddSection(data.value as string)}
              selectOnBlur={false}
              onChange={(e, data) => {
                const toSectionRef = _.find(
                  eligibleSections,
                  s => s.ref.id === data.value
                )?.ref;
                if (toSectionRef) {
                  onChange(toSectionRef);
                }
              }}
            />
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    </div>
  );
};
